import { useEffect, useRef, useState } from 'react';
import { WifiOff, Check, RefreshCw } from 'lucide-react';
import { useCurrentProject } from '../projects/currentProject';
import { useSyncStatus } from './useSyncStatus';

const OFFLINE_GRACE_MS = 3000; // don't flap the banner on a brief blip
const TOAST_MS = 3000;

/**
 * The heavy connection signals that complement the facepile pill (the compact
 * form): a non-blocking offline banner on a sustained disconnect (edits still
 * apply locally via the Y.Doc and flush on reconnect — y-indexeddb + the
 * provider replay), and a transient "back online" toast once it reconnects.
 * Hidden entirely when live sync is off. Signal-only — no second write path
 * (spec Open-Q2).
 */
export function ConnectionStatus() {
  const status = useSyncStatus();
  const [offline, setOffline] = useState(false);
  const [toast, setToast] = useState(false);
  const wasDisconnected = useRef(false);

  // Offline banner after a sustained disconnect; clears as soon as we're not
  // disconnected. Track that we dropped so reconnect can toast.
  useEffect(() => {
    if (status === 'disconnected') {
      wasDisconnected.current = true;
      const t = window.setTimeout(() => setOffline(true), OFFLINE_GRACE_MS);
      return () => window.clearTimeout(t);
    }
    setOffline(false);
    if (status === 'connected' && wasDisconnected.current) {
      wasDisconnected.current = false;
      setToast(true);
      const t = window.setTimeout(() => setToast(false), TOAST_MS);
      return () => window.clearTimeout(t);
    }
    return undefined;
  }, [status]);

  if (status === null) return null;

  return (
    <>
      {offline && (
        <div
          role="status"
          aria-live="assertive"
          className="fixed left-1/2 top-3 z-[80] -translate-x-1/2 flex items-center gap-2 rounded-full border border-[var(--color-menu-border)] bg-[var(--color-surface)] px-3.5 py-1.5 text-[13px] text-[var(--color-text)] shadow-[var(--shadow-2)]"
        >
          <WifiOff size={15} className="text-[var(--color-danger)]" />
          Offline — changes won&rsquo;t be saved until you reconnect.
        </div>
      )}
      {toast && (
        <div
          role="status"
          aria-live="polite"
          data-anim
          className="cf-pop-in fixed bottom-20 left-1/2 z-[80] -translate-x-1/2 flex items-center gap-2 rounded-full bg-[var(--color-surface)] px-3.5 py-1.5 text-[13px] text-[var(--color-text)] shadow-[var(--shadow-2)]"
        >
          <Check size={15} className="text-[var(--color-accent-2)]" />
          Back online — synced.
        </div>
      )}
    </>
  );
}

/**
 * Shown when the server rejected a REST save because the project is owned by
 * live sync while this client runs with sync opted out (Phase 3 cutover).
 * Edits stay cached locally; the only way forward is reloading into live sync.
 */
export function SyncUpgradeBanner() {
  const required = useCurrentProject((s) => s.syncUpgradeRequired);
  if (!required) return null;
  return (
    <div
      role="alert"
      className="fixed left-1/2 top-3 z-[80] -translate-x-1/2 flex items-center gap-2 rounded-full border border-[var(--color-menu-border)] bg-[var(--color-surface)] px-3.5 py-1.5 text-[13px] text-[var(--color-text)] shadow-[var(--shadow-2)]"
    >
      <RefreshCw size={15} className="text-[var(--color-accent)]" />
      This project now uses live sync — reload to keep saving.
      <button
        type="button"
        onClick={() => {
          localStorage.removeItem('codeframe.liveSync'); // clear the opt-out
          location.reload();
        }}
        className="ml-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-[var(--color-accent)] text-white hover:opacity-90"
      >
        Reload
      </button>
    </div>
  );
}
